import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { TextMultipleLangItem } from '@adornis/buchungsheld-db/entities/TextMultipleLangItem.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';

@Entity()
export class Company extends MongoEntity {
  static override _class = 'Company';
  static override _collectionName = 'companies';

  @Field(() => [ID], { default: v => v ?? [] })
  userIDs: Maybe<string[]>;

  @validate(options => nonOptional()({ ...options, value: options.value?.trim() }))
  @Field(() => String)
  name: Maybe<string>;

  @Field(() => String)
  address: Maybe<string>;

  @Field(() => String)
  sector: Maybe<string>;

  @Field(() => String)
  services: Maybe<string>;

  @Field(() => [TextMultipleLangItem])
  cultureAndValues: Maybe<TextMultipleLangItem[]>;

  @Field(() => [TextMultipleLangItem])
  benefits: Maybe<TextMultipleLangItem[]>;

  @Field(() => String)
  website: Maybe<string>;

  @Field(() => String)
  logoID: Maybe<string>;

  @Field(() => [SocialNetwork])
  socialNetworks: Maybe<SocialNetwork[]>;

  @Field(() => Boolean, { default: v => v ?? true })
  isActive: Maybe<boolean>;

  static override async beforeCreate(entity: Company): Promise<void> {
    await entity.validator.validateKeys(entity, ['name']);
    return super.beforeCreate(entity);
  }

  static override async transformBeforeUpdate(oldEntity: PartialEntityData<Company>, update: Company): Promise<void> {
    // Entity instances expose every field; the serialized update contains only supplied values.
    if ('name' in update.toJSON()) await update.validator.validateKeys(update, ['name']);
    return super.transformBeforeUpdate(oldEntity, update);
  }
}

@Entity()
export class SocialNetwork extends AdornisEntity {
  static override _class = 'SocialNetwork';

  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() })
  _id: Maybe<string>;

  @Field(() => String)
  socialNetwork: Maybe<string>;

  @Field(() => String)
  link: Maybe<string>;
}
